import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProductGrid from '../product-catalog-management/components/ProductGrid';
import ProductFormModal from '../product-catalog-management/components/ProductFormModal';
import InventorySync from '../product-catalog-management/components/InventorySync';

const InventoryManagementDashboard = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, isStaff, loading: authLoading } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStockStatus, setFilterStockStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [inventoryAlerts, setInventoryAlerts] = useState({
    lowStock: [],
    outOfStock: [],
    reorderNeeded: []
  });
  const [stats, setStats] = useState({
    totalProducts: 0,
    lowStockAlerts: 0,
    outOfStockItems: 0,
    inventoryValue: 0
  });

  useEffect(() => {
    if (!authLoading && (!user || (!isGymOwner && !isStaff))) {
      navigate('/member-dashboard');
    }
  }, [user, isGymOwner, isStaff, authLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && (isGymOwner || isStaff)) {
      loadInventoryData();
    }
  }, [currentTenant, isGymOwner, isStaff, filterCategory, filterStockStatus, searchQuery]);

  const loadInventoryData = async () => {
    setLoading(true);
    try {
      // Load categories
      const { data: categoriesData, error: categoriesError } = await supabase
        ?.from('pos_categories')
        ?.select('*')
        ?.eq('tenant_id', currentTenant?.id)
        ?.eq('is_active', true)
        ?.order('display_order', { ascending: true });

      if (categoriesError) throw categoriesError;
      setCategories(categoriesData || []);

      // Load products with inventory tracking
      let query = supabase
        ?.from('pos_products')
        ?.select('*, pos_categories(name), user_profiles:created_by(full_name)')
        ?.eq('tenant_id', currentTenant?.id)
        ?.order('updated_at', { ascending: false });

      if (filterCategory !== 'all') {
        query = query?.eq('category_id', filterCategory);
      }

      if (filterStockStatus === 'low') {
        query = query?.lte('stock_quantity', supabase?.raw('low_stock_threshold'));
      } else if (filterStockStatus === 'out') {
        query = query?.eq('stock_quantity', 0);
      }

      if (searchQuery?.trim()) {
        query = query?.or(`name.ilike.%${searchQuery}%,sku.ilike.%${searchQuery}%,barcode.ilike.%${searchQuery}%`);
      }

      const { data: productsData, error: productsError } = await query;

      if (productsError) throw productsError;
      setProducts(productsData || []);

      // Calculate stats and alerts
      const totalProducts = productsData?.length || 0;
      const lowStockItems = productsData?.filter(p => 
        p?.track_inventory && p?.stock_quantity > 0 && p?.stock_quantity <= p?.low_stock_threshold
      ) || [];
      const outOfStockItems = productsData?.filter(p => 
        p?.track_inventory && p?.stock_quantity === 0
      ) || [];
      const reorderNeeded = productsData?.filter(p => 
        p?.track_inventory && p?.stock_quantity <= (p?.low_stock_threshold / 2)
      ) || [];
      const inventoryValue = productsData?.reduce((sum, p) => 
        sum + (p?.stock_quantity * p?.price), 0
      ) || 0;

      setStats({
        totalProducts,
        lowStockAlerts: lowStockItems?.length,
        outOfStockItems: outOfStockItems?.length,
        inventoryValue
      });

      setInventoryAlerts({
        lowStock: lowStockItems,
        outOfStock: outOfStockItems,
        reorderNeeded: reorderNeeded
      });

    } catch (error) {
      console.error('Load inventory error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setShowProductModal(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm('Are you sure you want to delete this product?')) return;

    try {
      const { error } = await supabase
        ?.from('pos_products')
        ?.update({ is_active: false })
        ?.eq('id', productId);

      if (error) throw error;
      loadInventoryData();
    } catch (error) {
      console.error('Delete product error:', error);
      alert('Failed to delete product');
    }
  };

  const handleProductSaved = () => {
    setShowProductModal(false);
    setSelectedProduct(null);
    loadInventoryData();
  };

  const handleInventoryStockUpdate = (updatedProduct, action) => {
    if (action === 'delete') {
      setProducts(prev => prev?.filter(p => p?.id !== updatedProduct?.id));
    } else {
      setProducts(prev => {
        const index = prev?.findIndex(p => p?.id === updatedProduct?.id);
        if (index !== -1) {
          const updated = [...prev];
          updated[index] = { ...updated?.[index], ...updatedProduct };
          return updated;
        } else {
          return [updatedProduct, ...prev];
        }
      });
    }
    
    // Recalculate stats
    setTimeout(() => loadInventoryData(), 500);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading inventory...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-4 py-6 pb-24 lg:pb-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-bold text-foreground">Inventory Management</h1>
            <div className="flex items-center gap-2">
              <InventorySync 
                tenantId={currentTenant?.id}
                onStockUpdate={handleInventoryStockUpdate}
              />
              <Button
                variant="outline"
                onClick={() => navigate('/bulk-product-import-system')}
              >
                <Icon name="Upload" size={18} className="mr-2" />
                Bulk Import
              </Button>
            </div>
          </div>
          <p className="text-muted-foreground">
            Real-time inventory oversight with comprehensive stock management
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-card rounded-lg p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Total Products</span>
              <Icon name="Package" size={20} className="text-primary" />
            </div>
            <p className="text-2xl font-bold text-foreground">{stats?.totalProducts}</p>
            <p className="text-xs text-muted-foreground mt-1">Active inventory items</p>
          </div>

          <div className="bg-card rounded-lg p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Low Stock Alerts</span>
              <Icon name="AlertTriangle" size={20} className="text-warning" />
            </div>
            <p className="text-2xl font-bold text-warning">{stats?.lowStockAlerts}</p>
            <p className="text-xs text-muted-foreground mt-1">Items need restocking</p>
          </div>

          <div className="bg-card rounded-lg p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Out of Stock</span>
              <Icon name="XCircle" size={20} className="text-danger" />
            </div>
            <p className="text-2xl font-bold text-danger">{stats?.outOfStockItems}</p>
            <p className="text-xs text-muted-foreground mt-1">Items unavailable</p>
          </div>

          <div className="bg-card rounded-lg p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Inventory Value</span>
              <Icon name="DollarSign" size={20} className="text-success" />
            </div>
            <p className="text-2xl font-bold text-foreground">${stats?.inventoryValue?.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground mt-1">Total stock value</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Product Grid */}
          <div className="lg:col-span-2">
            {/* Filters */}
            <div className="bg-card rounded-lg p-4 border border-border mb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Search Products
                  </label>
                  <div className="relative">
                    <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e?.target?.value)}
                      placeholder="Search by name, SKU, barcode..."
                      className="w-full pl-10 pr-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Category
                  </label>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e?.target?.value)}
                    className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                  >
                    <option value="all">All Categories</option>
                    {categories?.map((cat) => (
                      <option key={cat?.id} value={cat?.id}>{cat?.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Stock Status
                  </label>
                  <select
                    value={filterStockStatus}
                    onChange={(e) => setFilterStockStatus(e?.target?.value)}
                    className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                  >
                    <option value="all">All Stock Levels</option>
                    <option value="low">Low Stock</option>
                    <option value="out">Out of Stock</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Product Grid */}
            <ProductGrid 
              products={products}
              onEdit={handleEditProduct}
              onDelete={handleDeleteProduct}
            />
          </div>

          {/* Critical Alerts Panel */}
          <div className="lg:col-span-1">
            <div className="bg-card rounded-lg border border-border p-4 sticky top-6">
              <h2 className="text-lg font-bold text-foreground mb-4 flex items-center">
                <Icon name="Bell" size={20} className="mr-2 text-warning" />
                Critical Alerts
              </h2>

              {/* Out of Stock */}
              {inventoryAlerts?.outOfStock?.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-danger mb-2 flex items-center">
                    <Icon name="XCircle" size={16} className="mr-1" />
                    Out of Stock ({inventoryAlerts?.outOfStock?.length})
                  </h3>
                  <div className="space-y-2">
                    {inventoryAlerts?.outOfStock?.slice(0, 5)?.map((product) => (
                      <div key={product?.id} className="bg-danger/10 rounded p-2 border border-danger/20">
                        <p className="text-sm font-medium text-foreground">{product?.name}</p>
                        <p className="text-xs text-muted-foreground">SKU: {product?.sku}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Low Stock */}
              {inventoryAlerts?.lowStock?.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-warning mb-2 flex items-center">
                    <Icon name="AlertTriangle" size={16} className="mr-1" />
                    Low Stock ({inventoryAlerts?.lowStock?.length})
                  </h3>
                  <div className="space-y-2">
                    {inventoryAlerts?.lowStock?.slice(0, 5)?.map((product) => (
                      <div key={product?.id} className="bg-warning/10 rounded p-2 border border-warning/20">
                        <p className="text-sm font-medium text-foreground">{product?.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Stock: {product?.stock_quantity} / Threshold: {product?.low_stock_threshold}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Reorder Recommendations */}
              {inventoryAlerts?.reorderNeeded?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-primary mb-2 flex items-center">
                    <Icon name="ShoppingCart" size={16} className="mr-1" />
                    Reorder Needed ({inventoryAlerts?.reorderNeeded?.length})
                  </h3>
                  <div className="space-y-2">
                    {inventoryAlerts?.reorderNeeded?.slice(0, 5)?.map((product) => (
                      <div key={product?.id} className="bg-primary/10 rounded p-2 border border-primary/20">
                        <p className="text-sm font-medium text-foreground">{product?.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Current: {product?.stock_quantity} units
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {inventoryAlerts?.outOfStock?.length === 0 && 
               inventoryAlerts?.lowStock?.length === 0 && 
               inventoryAlerts?.reorderNeeded?.length === 0 && (
                <div className="text-center py-8">
                  <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">All inventory levels healthy</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <MobileBottomNav />

      {/* Product Modal */}
      {showProductModal && (
        <ProductFormModal
          product={selectedProduct}
          tenantId={currentTenant?.id}
          userId={user?.id}
          onClose={() => {
            setShowProductModal(false);
            setSelectedProduct(null);
          }}
          onSaved={handleProductSaved}
        />
      )}
    </div>
  );
};

export default InventoryManagementDashboard;